import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { BookCollectionService, BookCollection } from '../services/bookCollectionService';
import AddBooksModal from '../components/AddBooksModal';
import CreateCollectionBatchDialog from '../components/CreateCollectionBatchDialog';
import CloneCollectionDialog from '../components/CloneCollectionDialog';
import SortableBookList, { getBookId } from '../components/SortableBookList';
import CollectionSectionVisibility from '../components/CollectionSectionVisibility';
import { arrayMove } from '@dnd-kit/sortable';
import toast from 'react-hot-toast';

const BookCollectionDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [collection, setCollection] = useState<BookCollection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddBooksModal, setShowAddBooksModal] = useState(false);
  const [showCreateBatchDialog, setShowCreateBatchDialog] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [removingBookId, setRemovingBookId] = useState<string | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderSaved, setOrderSaved] = useState(false);

  useEffect(() => {
    if (id) {
      loadCollection(id);
    }
  }, [id]);

  const loadCollection = async (collectionId: string) => {
    try {
      setLoading(true);
      const response = await BookCollectionService.getCollection(collectionId, true);
      
      if (response && response.data) {
        setCollection(response.data);
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to load collection:', err);
      setError('Không thể tải thông tin bộ sách. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const handlePublishToggle = async () => {
    if (!collection || !id) return;

    try {
      const isPublished = collection.status === 'published';
      const response = isPublished 
        ? await BookCollectionService.unpublishCollection(id)
        : await BookCollectionService.publishCollection(id);

      if (response && response.success && response.data) {
        setCollection(response.data);
      }
    } catch (err: any) {
      console.error('Failed to toggle publish status:', err);
      setError(err?.response?.data?.message || 'Không thể thay đổi trạng thái xuất bản.');
    }
  };

  const handleRemoveBook = async (bookId: string) => {
    if (!id) return;

    try {
      setRemovingBookId(bookId);
      const response = await BookCollectionService.removeBookFromCollection(id, bookId);
      
      if (response && response.success) {
        // Reload collection to get updated books list
        await loadCollection(id);
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to remove book:', err);
      setError(err?.response?.data?.message || 'Không thể xóa sách khỏi bộ sách.');
    } finally {
      setRemovingBookId(null);
    }
  };

  const handleAddBooks = async (bookIds: string[]) => {
    if (!id) return;

    try {
      // Add each book to the collection
      for (const bookId of bookIds) {
        await BookCollectionService.addBookToCollection(id, bookId);
      }
      
      // Reload collection to get updated books list
      await loadCollection(id);
      setShowAddBooksModal(false);
      setError(null);
    } catch (err: any) {
      console.error('Failed to add books:', err);
      setError(err?.response?.data?.message || 'Không thể thêm sách vào bộ sách.');
    }
  };

  // Đổi chỗ ngay trên màn hình rồi mới lưu; server từ chối thì trả về thứ tự cũ
  const handleReorder = async (oldIndex: number, newIndex: number) => {
    if (!id || !collection?.books || savingOrder) return;

    const previous = collection;
    const books = arrayMove(collection.books, oldIndex, newIndex);
    setCollection({ ...collection, books });
    setOrderSaved(false);

    try {
      setSavingOrder(true);
      const response = await BookCollectionService.updateBooksOrder(id, books.map(getBookId));
      if (response?.success && response.data) {
        setCollection((current) => current && { ...current, book_ids: response.data.book_ids });
        setOrderSaved(true);
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to update books order:', err);
      setCollection(previous);
      setError(err?.response?.data?.message || 'Không thể lưu thứ tự sách. Vui lòng thử lại.');
    } finally {
      setSavingOrder(false);
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
  };

  const getStatusDisplay = (status: string) => {
    return status === 'published' ? 'Đã xuất bản' : 'Nháp';
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="animate-spin inline-block w-8 h-8 border-4 border-current border-t-transparent text-blue-600 rounded-full"></div>
          <p className="mt-4 text-gray-600">Đang tải...</p>
        </div>
      </div>
    );
  }

  if (error && !collection) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <svg className="w-12 h-12 mx-auto text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Có lỗi xảy ra</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => navigate('/books/collections')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Quay lại danh sách
          </button>
        </div>
      </div>
    );
  }

  if (!collection) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <button
            onClick={() => navigate('/books/collections')}
            className="text-gray-600 hover:text-gray-900 flex items-center mb-4"
          >
            <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Quay lại
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{collection.title}</h1>
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(collection.status)}`}>
              {getStatusDisplay(collection.status)}
            </span>
            <span className="text-sm text-gray-500">
              {collection.books?.length || 0} sách
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowCreateBatchDialog(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Tạo mã kích hoạt
          </button>
          <button
            onClick={() => setShowCloneDialog(true)}
            className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Nhân bản
          </button>
          <button
            onClick={handlePublishToggle}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              collection.status === 'published'
                ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {collection.status === 'published' ? 'Hủy xuất bản' : 'Xuất bản'}
          </button>
          <Link
            to={`/books/collections/${id}/edit`}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Chỉnh sửa
          </Link>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Collection Info */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="md:flex">
          {/* Cover Image */}
          <div className="md:w-1/3 lg:w-1/4">
            <div className="h-64 md:h-full bg-gradient-to-br from-blue-500 to-purple-600 relative">
              {collection.cover_image_url ? (
                <img 
                  src={collection.cover_image_url} 
                  alt={collection.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <svg className="h-24 w-24 text-white opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="md:w-2/3 lg:w-3/4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Mô tả</h2>
            {collection.description ? (
              <p className="text-gray-600 whitespace-pre-wrap">{collection.description}</p>
            ) : (
              <p className="text-gray-400 italic">Chưa có mô tả</p>
            )}
          </div>
        </div>
      </div>

      {/* Books Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Danh sách sách ({collection.books?.length || 0})
            </h2>
            {(collection.books?.length || 0) > 1 && (
              <p className="text-sm text-gray-500 mt-1" aria-live="polite">
                {savingOrder
                  ? 'Đang lưu thứ tự…'
                  : orderSaved
                    ? 'Đã lưu thứ tự sách.'
                    : 'Kéo biểu tượng ⋮⋮ để sắp xếp vị trí sách trong bộ.'}
              </p>
            )}
          </div>
          <button
            onClick={() => setShowAddBooksModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <svg className="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Thêm sách
          </button>
        </div>

        {collection.books && collection.books.length > 0 ? (
          <SortableBookList
            books={collection.books}
            disabled={savingOrder || removingBookId !== null}
            removingBookId={removingBookId}
            onReorder={handleReorder}
            onRemove={handleRemoveBook}
          />
        ) : (
          <div className="text-center py-12">
            <svg className="h-16 w-16 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có sách nào</h3>
            <p className="text-gray-500 mb-4">Thêm sách vào bộ sách để bắt đầu.</p>
            <button
              onClick={() => setShowAddBooksModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Thêm sách
            </button>
          </div>
        )}
      </div>

      {/* Bật/tắt tiết theo từng sách trong bộ */}
      {(collection.books?.length || 0) > 0 && (
        <CollectionSectionVisibility collectionId={id!} books={collection.books || []} />
      )}

      {/* Add Books Modal */}
      {showAddBooksModal && (
        <AddBooksModal
          collectionId={id!}
          existingBookIds={collection.book_ids || []}
          onClose={() => setShowAddBooksModal(false)}
          onAdd={handleAddBooks}
        />
      )}

      {/* Create Batch Dialog */}
      {showCreateBatchDialog && (
        <CreateCollectionBatchDialog
          open={showCreateBatchDialog}
          onOpenChange={setShowCreateBatchDialog}
          collectionId={id!}
          collectionTitle={collection.title}
          onSuccess={() => {
            // Optionally reload data or show success message
            toast.success('Mã kích hoạt đã được tạo thành công!');
          }}
        />
      )}

      <CloneCollectionDialog
        open={showCloneDialog}
        onOpenChange={setShowCloneDialog}
        collection={collection}
        onSuccess={(newCollection) => navigate(`/books/collections/${newCollection.collection_id || newCollection._id}`)}
      />
    </div>
  );
};

export default BookCollectionDetailPage;

